import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Trophy, Globe, MapPin, Compass, Star, Heart, Target,
  Plane, Mountain, Palmtree, Camera, Users, Crown, Sparkles,
  Flame, Zap, Award, Medal, Lock, Check, ChevronRight,
  Map, Building, Send, User, Eye, Share2
} from "lucide-react";

// Icon mapping for badge types
const ICON_MAP = {
  countries: Globe,
  continents: Map,
  world_percentage: Compass,
  completed: Star,
  category_travel: Plane,
  category_adventures: Mountain,
  category_experiences: Sparkles,
  category_romance: Heart,
  category_goals: Target,
  streak: Flame,
  partner_linked: Heart,
  photos_added: Camera,
  group_member: Users,
  premium_subscriber: Crown,
  founding_member: Crown,
  invites_sent: Send,
  profile_complete: User,
  public_profile: Eye,
  duo_shared_trips: Heart,
  duo_same_continent: Globe,
  memories_created: Camera,
  cities_visited: Building,
};

const TIER_COLORS = {
  bronze: {
    bg: "bg-amber-900/20",
    border: "border-amber-700/30",
    text: "text-amber-700",
    icon: "text-amber-600",
    glow: ""
  },
  silver: {
    bg: "bg-slate-300/20",
    border: "border-slate-400/30",
    text: "text-slate-500",
    icon: "text-slate-400",
    glow: ""
  },
  gold: {
    bg: "bg-yellow-500/20",
    border: "border-yellow-500/30",
    text: "text-yellow-600",
    icon: "text-yellow-500",
    glow: "shadow-yellow-500/10"
  },
  platinum: {
    bg: "bg-purple-500/20",
    border: "border-purple-400/30",
    text: "text-purple-500",
    icon: "text-purple-400",
    glow: "shadow-purple-500/10"
  },
  diamond: {
    bg: "bg-cyan-400/20",
    border: "border-cyan-400/30",
    text: "text-cyan-500",
    icon: "text-cyan-400",
    glow: "shadow-cyan-400/20"
  }
};

const CATEGORY_LABELS = {
  countries: "Countries",
  continents: "Continents",
  world: "World",
  items: "Items",
  categories: "Categories",
  streaks: "Streaks",
  social: "Social",
  profile: "Profile",
  special: "Special",
  duo: "Duo",
  memories: "Memories",
  cities: "Cities",
};

const STAT_MAP = {
  countries: "countries_visited",
  continents: "continents_visited",
  world_percentage: "world_percentage",
  completed: "items_completed",
  category_travel: "travel_completed",
  category_adventures: "adventures_completed",
  category_experiences: "experiences_completed",
  category_romance: "romance_completed",
  category_goals: "goals_completed",
  streak: "longest_streak",
  partner_linked: "has_partner",
  photos_added: "total_photos",
  group_member: "in_group",
  premium_subscriber: "is_premium",
  founding_member: "is_founding",
  invites_sent: "invites_sent",
  profile_complete: "profile_complete",
  public_profile: "public_profile",
  duo_shared_trips: "duo_shared_trips",
  duo_same_continent: "duo_same_continent",
  memories_created: "memories_created",
  cities_visited: "cities_visited",
};

export const Achievements = ({ isOpen, onClose }) => {
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [registry, setRegistry] = useState({});
  const [totalFromBackend, setTotalFromBackend] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    if (isOpen) {
      fetchAchievements();
    }
  }, [isOpen]);

  const fetchAchievements = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/achievements`);
      setAchievements(res.data.achievements || []);
      setStats(res.data.stats || {});
      setRegistry(res.data.registry || {});
      setTotalFromBackend(res.data.total_achievements || Object.keys(res.data.registry || {}).length);
    } catch (error) {
      console.error("Failed to load achievements");
    } finally {
      setLoading(false);
    }
  };

  const unlockedIds = new Set(achievements.map(a => a.achievement_id));
  const totalUnlocked = unlockedIds.size;
  const totalAchievements = totalFromBackend || Object.keys(registry).length;
  const progressPercent = totalAchievements > 0 ? Math.round((totalUnlocked / totalAchievements) * 100) : 0;

  // Derive categories from registry
  const allCategories = ["all", ...Array.from(new Set(Object.values(registry).map(r => r.category))).sort()];

  // Build achievement list from registry
  const registryEntries = Object.entries(registry).map(([id, def]) => ({
    id,
    ...def,
  }));

  const filteredAchievements = registryEntries.filter(
    a => selectedCategory === "all" || a.category === selectedCategory
  );

  const getProgress = (achievement) => {
    if (!stats) return 0;
    const statKey = STAT_MAP[achievement.type];
    if (!statKey) return 0;
    const val = stats[statKey];
    if (typeof val === "boolean") return val ? 1 : 0;
    return Math.min(val || 0, achievement.requirement);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-none rounded-2xl p-0 max-h-[85vh] overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Achievements
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Track your progress and unlock badges
          </DialogDescription>
          
          {/* Overall Progress */}
          <div className="mt-4 p-4 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-charcoal" data-testid="achievement-progress-text">
                {totalUnlocked} of {totalAchievements} Achievements
              </span>
              <span className="text-sm font-bold text-yellow-600">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2 bg-charcoal/10" />
          </div>
        </DialogHeader>

        {/* Category Filter */}
        <div className="px-6 py-3 border-b border-charcoal/5 overflow-x-auto">
          <div className="flex gap-2">
            {allCategories.map((cat) => (
              <Button
                key={cat}
                type="button"
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-full whitespace-nowrap ${
                  selectedCategory === cat 
                    ? "bg-primary text-white" 
                    : "border-charcoal/10 text-charcoal/70"
                }`}
              >
                {cat === "all" ? "All" : (CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1))}
              </Button>
            ))}
          </div>
        </div>

        <ScrollArea className="max-h-[55vh]">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-yellow-500/50 mx-auto mb-4 animate-pulse" />
                <p className="text-muted-foreground">Loading achievements...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAchievements.map((achievement) => {
                  const isUnlocked = unlockedIds.has(achievement.id);
                  const tier = TIER_COLORS[achievement.tier] || TIER_COLORS.bronze;
                  const Icon = ICON_MAP[achievement.type] || Trophy;
                  const progress = getProgress(achievement);
                  const pct = Math.round((progress / achievement.requirement) * 100);

                  return (
                    <div
                      key={achievement.id}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        isUnlocked 
                          ? `${tier.bg} ${tier.border} ${tier.glow} shadow-lg` 
                          : "bg-charcoal/5 border-charcoal/10 opacity-60"
                      }`}
                      data-testid={`achievement-${achievement.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isUnlocked ? tier.bg : "bg-charcoal/10"
                        }`}>
                          {isUnlocked ? (
                            <Icon className={`w-6 h-6 ${tier.icon}`} />
                          ) : (
                            <Lock className="w-5 h-5 text-charcoal/30" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${isUnlocked ? "text-charcoal" : "text-charcoal/50"}`}>
                              {achievement.title}
                            </h3>
                            {isUnlocked && (
                              <Check className={`w-4 h-4 ${tier.icon}`} />
                            )}
                          </div>
                          <p className={`text-sm ${isUnlocked ? "text-muted-foreground" : "text-charcoal/40"}`}>
                            {achievement.description}
                          </p>

                          {!isUnlocked && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-charcoal/40 mb-1">
                                <span>{progress} / {achievement.requirement}</span>
                                <span>{pct}%</span>
                              </div>
                              <div className="h-1.5 bg-charcoal/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-charcoal/30 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${
                          isUnlocked ? `${tier.bg} ${tier.text}` : "bg-charcoal/10 text-charcoal/40"
                        }`}>
                          {achievement.tier}
                        </span>
                      </div>

                      {isUnlocked && (
                        <div className="mt-2 pt-2 border-t border-charcoal/5">
                          <span className="text-xs text-muted-foreground">
                            Unlocked {new Date(achievements.find(a => a.achievement_id === achievement.id)?.unlocked_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Achievement notification toast component
export const AchievementUnlockedToast = ({ achievement }) => {
  const tier = TIER_COLORS[achievement.tier] || TIER_COLORS.bronze;
  const Icon = ICON_MAP[achievement.type] || Trophy;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${tier.bg} ${tier.border} border`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tier.bg}`}>
        <Icon className={`w-5 h-5 ${tier.icon}`} />
      </div>
      <div>
        <p className="font-medium text-charcoal text-sm">Achievement Unlocked!</p>
        <p className={`text-xs ${tier.text}`}>{achievement.title}</p>
      </div>
    </div>
  );
};

export default Achievements;
